import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { NotificationItem } from '../../types/notifications';
import { notificationApiService } from '../../services/notificationApiService';
import { apiService } from '../../lib/api';
// import TibioCareIcon from '../Icons/TibioCareIcon';
import ShoulderIcon from '../Icons/ShoulderIcon';
// import { useAuth } from '../../hooks/useAuth';
import { themeClasses } from '../../lib/theme';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  onCountUpdate?: () => void;
}

export default function NotificationCenter({ isOpen, onClose, onCountUpdate }: NotificationCenterProps) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasShoulderTag, setHasShoulderTag] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  // const { user } = useAuth();
  const navigate = useNavigate();

  // Set default tab based on shoulder preference
  const [activeTab, setActiveTab] = useState<'reconstructions' | 'community'>('community');

  useEffect(() => {
    if (isOpen) {
      loadTagPreferences();
      loadNotifications();

      // Prevent body scroll when notification panel is open
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const loadTagPreferences = async () => {
    try {
      // Fetch tag preferences from API using authenticated apiService
      const response = await apiService.request<{ tags: Array<{ id: number; text: string }> }>('/api/v1/user/tag_preferences');
      const tags = response.tags || [];
      const hasShoulder = tags.some(tag => tag.text?.toLowerCase() === 'shoulder');
      setHasShoulderTag(hasShoulder);

      // Update active tab based on shoulder preference
      if (hasShoulder && activeTab === 'community') {
        setActiveTab('reconstructions');
      } else if (!hasShoulder && activeTab === 'reconstructions') {
        // Force back to community if user doesn't have shoulder anymore
        setActiveTab('community');
      }
    } catch (error) {
      console.error('Failed to load tag preferences:', error);
      setHasShoulderTag(false);
      // Force community tab on error
      setActiveTab('community');
    }
  };

  // Auto-refresh notifications every 30 seconds when panel is open
  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(loadNotifications, 30 * 1000); // 30 seconds
    return () => clearInterval(interval);
  }, [isOpen]);

  // Mark visible notifications as seen when activeTab changes or after notifications load
  useEffect(() => {
    if (isOpen && !loading && notifications.length > 0) {
      // Use a small delay to ensure filteredNotifications is updated
      const timer = setTimeout(() => {
        markVisibleNotificationsAsSeen();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [activeTab, notifications.length, isOpen, loading]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const allNotifications = await notificationApiService.getNotifications(0, 50);
      setNotifications(allNotifications);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark visible notifications as seen when viewing a tab
  const markVisibleNotificationsAsSeen = async () => {
    try {
      // Get unread notifications in the current tab
      const visibleUnreadNotifications = filteredNotifications.filter(notif => !notif.isRead);

      if (visibleUnreadNotifications.length === 0) {
        return; // Nothing to mark
      }

      // Mark each visible unread notification as seen
      const promises = visibleUnreadNotifications.map(notif =>
        notificationApiService.markAsRead(notif.id)
      );

      await Promise.all(promises);

      // Update local state - mark these notifications as read
      setNotifications(prev =>
        prev.map(notif => {
          const shouldMarkAsRead = visibleUnreadNotifications.some(vn => vn.id === notif.id);
          return shouldMarkAsRead ? { ...notif, isRead: true } : notif;
        })
      );

      // Update badge count in parent component
      onCountUpdate?.();
    } catch (error) {
      console.error('Failed to mark visible notifications as seen:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationApiService.markAsRead(notificationId);
      loadNotifications(); // Refresh the list
      onCountUpdate?.(); // Update badge count in parent
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Unused handler - kept for potential future use
  // const handleMarkAllAsRead = async () => {
  //   try {
  //     await notificationApiService.markAllAsRead();
  //     loadNotifications(); // Refresh the list
  //     onCountUpdate?.(); // Update badge count in parent
  //   } catch (error) {
  //     console.error('Failed to mark all notifications as read:', error);
  //   }
  // };

  const handleDeleteNotification = async (notificationId: string) => {
    try {
      // Optimistically remove from UI
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Call backend to delete
      await apiService.delete(`/api/v1/user/notifications/${notificationId}`);

      // Update badge count
      onCountUpdate?.();
    } catch (error) {
      console.error('Failed to delete notification:', error);
      // Reload notifications on error to restore state
      loadNotifications();
    }
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    // Mark as read when clicked
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id);
    }

    // Navigate based on notification type and data
    if (notification.actionData?.postId) {
      // Navigate to the specific post
      navigate(`/?post=${notification.actionData.postId}`);
      onClose(); // Close notification panel
    } else if (notification.actionUrl) {
      // Use direct URL if available
      navigate(notification.actionUrl);
      onClose();
    } else {
      // Fallback: navigate to home feed
      navigate('/');
      onClose();
    }
  };

  // Helper function to determine if notification is reconstruction-related
  const isReconstructionNotification = (notification: NotificationItem) => {
    // Check if this is explicitly marked as a reconstruction notification
    if (notification.caseId || notification.status) {
      return true;
    }

    // Explicit reconstruction type
    if (notification.type === 'reconstruction') {
      return true;
    }

    // Legacy: check type or content keywords
    return notification.type === 'exactech_news' ||
           notification.type === 'community_post' ||
           (notification.message && notification.message.toLowerCase().includes('reconstruction'));
  };

  // Get notification title (prefer caseId for reconstructions)
  const getNotificationTitle = (notification: NotificationItem) => {
    // For admin notifications, use the notification title itself
    if (notification.type === 'adminNotification') {
      return notification.title || 'Admin Announcement';
    }

    // For reconstruction notifications, use caseId directly as title
    if (notification.caseId) {
      return notification.caseId;
    }

    // Check if this is a reconstruction notification by checking content
    if (isReconstructionNotification(notification)) {
      // Try to extract case ID from message using regex patterns
      const message = notification.message || '';

      // Pattern 1: "case TEST-123456" or "Case TEST-123456"
      const caseMatch1 = message.match(/case\s+([A-Z]+-\d+)/i);
      if (caseMatch1) {
        return caseMatch1[1];
      }

      // Pattern 2: "TEST-123456" (standalone case ID)
      const caseMatch2 = message.match(/\b([A-Z]+-\d+)\b/);
      if (caseMatch2) {
        return caseMatch2[1];
      }

      // Fallback to generic reconstruction title
      return 'Reconstruction';
    }

    // For community notifications, show the actor's name (who performed the action)
    if (notification.actor?.name) {
      return notification.actor.name;
    }
    if (notification.sender?.name) {
      return notification.sender.name;
    }

    // Fallback to post title if available
    if (notification.actionData?.postTitle) {
      return notification.actionData.postTitle;
    }

    // Fallback to generic title
    return 'New Notification';
  };

  // Get status color for reconstruction notifications
  const getStatusColor = (status?: string) => {
    if (!status) return 'text-gray-500';

    switch (status) {
      case 'Reconstruction Ready':
        return 'text-green-600 dark:text-green-500';
      case 'Planning Only':
        return 'text-yellow-600 dark:text-yellow-500';
      case 'Rejected':
        return 'text-red-600 dark:text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  // Get status background color for badges
  const getStatusBgColor = (status?: string) => {
    if (!status) return 'bg-gray-100 dark:bg-gray-800';

    switch (status) {
      case 'Reconstruction Ready':
        return 'bg-green-100 dark:bg-green-900/30';
      case 'Planning Only':
        return 'bg-yellow-100 dark:bg-yellow-900/30';
      case 'Rejected':
        return 'bg-red-100 dark:bg-red-900/30';
      default:
        return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  // Get border color for notifications (matching mobile)
  const getBorderColor = (notification: NotificationItem) => {
    const isReconstruction = isReconstructionNotification(notification);

    if (isReconstruction) {
      const message = notification.message?.toLowerCase() || '';

      // Check EXACT phrases from backend messages (most specific first)
      if (message.includes('was rejected')) {
        return '#EF4444'; // Red - "was rejected"
      }
      if (message.includes('complete for planning only')) {
        return '#F59E0B'; // Yellow - "is complete for planning only"
      }
      if (message.includes('is complete')) {
        return '#10B981'; // Green - "is complete"
      }

      // Fallback to status field if exists
      if (notification.status) {
        if (notification.status.includes('Reject')) return '#EF4444';
        if (notification.status.includes('Planning')) return '#F59E0B';
        if (notification.status.includes('Ready') || notification.status.includes('Complete')) return '#10B981';
      }

      return '#10B981'; // Default green for reconstructions
    }

    // Community notifications: green for comments/upvotes, yellow for polls, purple for admin
    if (notification.type === 'poll_vote') {
      return '#F59E0B'; // Yellow
    }

    if (notification.type === 'adminNotification') {
      return '#8B5CF6'; // Purple for admin notifications
    }

    return '#10B981'; // Green for comments/upvotes
  };

  // Filter notifications based on active tab
  const filteredNotifications = notifications.filter(notification => {
    // Exclude upvote notifications entirely
    if (notification.type === 'postUpvote' || notification.type === 'commentUpvote') {
      return false;
    }

    if (activeTab === 'reconstructions') {
      return isReconstructionNotification(notification);
    } else {
      // Community tab: comments, poll votes, admin notifications (no upvotes)
      return !isReconstructionNotification(notification);
    }
  });

  // Get counts for each tab (unread only, excluding upvotes)
  // const reconstructionCount = notifications.filter(n => isReconstructionNotification(n) && !n.isRead).length;
  const communityCount = notifications.filter(n =>
    !isReconstructionNotification(n) &&
    !n.isRead &&
    n.type !== 'postUpvote' &&
    n.type !== 'commentUpvote'
  ).length;

  // Get icon with color-coded background based on notification type
  // Community: green for comments/upvotes, yellow for polls
  const getNotificationIcon = (type: NotificationItem['type']) => {
    // Determine color based on type
    const isAdmin = type === 'adminNotification';
    const isPoll = type === 'poll_vote';
    const bgColor = isAdmin
      ? 'bg-purple-100 dark:bg-purple-900/30'
      : isPoll
      ? 'bg-yellow-100 dark:bg-yellow-900/30'
      : 'bg-green-100 dark:bg-green-900/30';
    const iconColor = isAdmin
      ? 'text-purple-600 dark:text-purple-400'
      : isPoll
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-healthy-green';

    // Admin notification icon (megaphone)
    if (isAdmin) {
      return (
        <div className={`w-12 h-12 ${bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
          <svg className={`w-6 h-6 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
            <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
          </svg>
        </div>
      );
    }

    // Default icon (thumbs up)
    return (
      <div className={`w-12 h-12 ${bgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
        <svg className={`w-6 h-6 ${iconColor}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
        </svg>
      </div>
    );
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      {/* Backdrop with blur - matching search modal */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Notification Center Panel - right side of screen */}
      <div className="fixed inset-y-0 right-0 w-96 max-w-[calc(100vw-2rem)] z-[10000]">
        <div className={`h-full ${themeClasses.bgPrimary} shadow-2xl border-l ${themeClasses.borderPrimary} flex flex-col`}>
        {/* Header */}
        <div className={`border-b ${themeClasses.borderPrimary}`}>
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-2">
              <h2 className={`text-lg font-semibold ${themeClasses.textPrimary}`}>Notifications</h2>
              <button
                onClick={() => setShowLegend(true)}
                className={`p-1 hover:${themeClasses.bgSecondary} rounded-full transition-colors`}
                title="Color Legend"
              >
                <svg className={`w-5 h-5 ${themeClasses.textTertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <button
              onClick={onClose}
              className={`p-1 hover:${themeClasses.bgSecondary} rounded-full transition-colors`}
            >
              <svg className={`w-5 h-5 ${themeClasses.textTertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className={`flex border-b ${themeClasses.borderPrimary}`}>
            {/* Only show reconstruction tab if user has shoulder in preferences */}
            {hasShoulderTag && (
              <button
                onClick={() => setActiveTab('reconstructions')}
                className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'reconstructions'
                    ? 'border-healthy-green text-healthy-green bg-green-50 dark:bg-green-900/20'
                    : `border-transparent ${themeClasses.textSecondary} hover:${themeClasses.textPrimary} hover:border-gray-300`
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <ShoulderIcon className="w-4 h-4" />
                  <span>Reconstructions</span>
                  {/* Remove count from reconstruction header as requested */}
                </div>
              </button>
            )}
            <button
              onClick={() => setActiveTab('community')}
              className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'community'
                  ? 'border-healthy-green text-healthy-green bg-green-50 dark:bg-green-900/20'
                  : `border-transparent ${themeClasses.textSecondary} hover:${themeClasses.textPrimary} hover:border-gray-300`
              }`}
            >
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
                <span>Community</span>
                {communityCount > 0 && (
                  <span className={`inline-flex items-center justify-center px-2 py-1 text-xs rounded-full ${
                    activeTab === 'community'
                      ? 'bg-healthy-green text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                    {communityCount}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-healthy-green mx-auto mb-4"></div>
              <p className={themeClasses.textSecondary}>Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                </svg>
              </div>
              <h3 className={`text-lg font-medium ${themeClasses.textPrimary} mb-2`}>
                No {activeTab === 'reconstructions' ? 'reconstruction' : 'community'} notifications
              </h3>
              <p className={`${themeClasses.textSecondary} text-sm`}>
                {activeTab === 'reconstructions'
                  ? "You'll see reconstruction-related notifications here"
                  : "You'll see community interactions like comments and likes here"
                }
              </p>
            </div>
          ) : (
            <div className={`divide-y ${themeClasses.borderSecondary}`}>
              {filteredNotifications.map((notification) => {
                const isReconstruction = isReconstructionNotification(notification);
                const statusColor = getStatusColor(notification.status);
                const statusBgColor = getStatusBgColor(notification.status);
                const borderColor = getBorderColor(notification);

                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 cursor-pointer hover:${themeClasses.bgSecondary} transition-colors border-l-4`}
                    style={{ borderLeftColor: borderColor }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon on left - show shoulder icon for reconstructions with color-coded background */}
                      {isReconstruction ? (
                        <div className={`w-12 h-12 ${statusBgColor} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <ShoulderIcon className="w-8 h-8" />
                        </div>
                      ) : (
                        getNotificationIcon(notification.type)
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0 overflow-hidden">
                        {/* Title - show patient ID for reconstructions */}
                        <h4 className={`text-base font-bold ${themeClasses.textPrimary} mb-2 break-words`}>
                          {getNotificationTitle(notification)}
                        </h4>

                        {/* Status badge for reconstructions */}
                        {isReconstruction && notification.status && (
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold mb-2 ${statusBgColor} ${statusColor} uppercase tracking-wide`}>
                            {notification.status}
                          </div>
                        )}

                        {/* Message */}
                        <p className={`text-sm ${themeClasses.textSecondary} mb-3 leading-relaxed break-words`}>
                          {notification.message}
                        </p>

                        {/* Bottom row - timestamp and "Click to view" */}
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-xs ${themeClasses.textTertiary} whitespace-nowrap`}>
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                          </span>
                          {!isReconstruction && notification.type !== 'adminNotification' && (
                            <span className="text-xs text-healthy-green flex items-center gap-1 flex-shrink-0">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              Click to view
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Delete button on right - matching mobile */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteNotification(notification.id);
                        }}
                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors flex-shrink-0"
                        title="Delete notification"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );

  // Legend Modal
  const legendModal = showLegend && (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setShowLegend(false)}
      />
      <div className={`relative z-10 ${themeClasses.bgPrimary} rounded-lg shadow-2xl p-6 max-w-sm mx-4`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${themeClasses.textPrimary}`}>
            Notification Color Legend
          </h3>
          <button
            onClick={() => setShowLegend(false)}
            className={`p-1 hover:${themeClasses.bgSecondary} rounded-full transition-colors`}
          >
            <svg className={`w-5 h-5 ${themeClasses.textTertiary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {activeTab === 'reconstructions' ? (
            <div>
              <h4 className={`text-sm font-medium ${themeClasses.textSecondary} mb-3`}>
                Reconstruction Status Colors
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-green-500 flex-shrink-0" />
                  <span className={`text-sm ${themeClasses.textPrimary}`}>
                    Reconstruction Ready
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-yellow-500 flex-shrink-0" />
                  <span className={`text-sm ${themeClasses.textPrimary}`}>
                    Planning Only
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-red-500 flex-shrink-0" />
                  <span className={`text-sm ${themeClasses.textPrimary}`}>
                    Rejected
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-blue-500 flex-shrink-0" />
                  <span className={`text-sm ${themeClasses.textPrimary}`}>
                    New/Updated
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h4 className={`text-sm font-medium ${themeClasses.textSecondary} mb-3`}>
                Community Notification Colors
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-green-500 flex-shrink-0" />
                  <span className={`text-sm ${themeClasses.textPrimary}`}>
                    Comments
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-yellow-500 flex-shrink-0" />
                  <span className={`text-sm ${themeClasses.textPrimary}`}>
                    Poll Votes
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded bg-purple-500 flex-shrink-0" />
                  <span className={`text-sm ${themeClasses.textPrimary}`}>
                    Admin Announcements
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={() => setShowLegend(false)}
          className="mt-6 w-full px-4 py-2 bg-healthy-green text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
        >
          Got it
        </button>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {legendModal && createPortal(legendModal, document.body)}
    </>
  );
}
